import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { apiFetch } from '../lib/api';
import { LayoutDashboard } from 'lucide-react';

interface Dashboard {
  id: number;
  name: string;
  description: string | null;
  collectionId: number;
}

export function Portal() {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ dashboards: Dashboard[] }>('/api/collections/root')
      .then((res) => {
        setDashboards(res.dashboards);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (error) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-red-600">Failed to load: {error}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      </div>
    );
  }

  if (dashboards.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="text-gray-500">No dashboards available yet.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Dashboards</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {dashboards.map((d) => (
            <button
              key={d.id}
              onClick={() => navigate(`/dashboard/${d.id}`)}
              className="flex flex-col gap-2 rounded-xl border bg-white p-4 text-left transition-colors hover:bg-gray-50 hover:border-gray-300"
            >
              <div className="flex items-center gap-2">
                <LayoutDashboard className="size-5 text-gray-400" />
                <span className="font-medium truncate">{d.name}</span>
              </div>
              {d.description && (
                <p className="text-sm text-gray-500 line-clamp-2">{d.description}</p>
              )}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
